import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect } from 'react';

import useLoggedUser from '../../../hooks/use-logged-user';
import usePageViewEvent from '../../../hooks/use-page-view-event';
import useOGMeta from '../../../utils/seo/ogmeta';
import useSchemaBreadcrumbs from '../../../utils/seo/schema-org/breadcrumbs';
import useSchema from '../../../utils/seo/schema-org/schema';
import useSchemaWebPage from '../../../utils/seo/schema-org/webpage';
import useHreflang from '../../../utils/seo/hreflang';

const VehicleSchema: FunctionComponent<{
  vehicle: TVehicle['car'];
  breadcrumbs: Array<{
    href: LinkProps;
    text: string;
  }>;
  images: Array<TImage>;
  sku: string
  uniqueVehicleInfo: TVehicle;
}> = ({ vehicle, breadcrumbs, images, sku, uniqueVehicleInfo }) => {
  const { t } = useTranslation();
  const user = useLoggedUser();

  const name = t('vehicle:head.title', {
    vehicle: `${vehicle.brand_name} ${vehicle.model_name}${vehicle.version_name || ''
      }`,
  });

  const description = t('vehicle:head.description', {
    vehicle: `${vehicle.brand_name} ${vehicle.model_name}${vehicle.version_name && ` ${vehicle.version_name}`
      }`,
  });

  const { locale = 'en', asPath, pathname, query } = useRouter();
  const localePath = locale !== 'en' ? `/${locale}` : '';
  const cleanAsPathForCanonicalURL = asPath.split('?')[0];

  const og = useOGMeta({
    title: name,
    description,
    url: process.env.NEXT_PUBLIC_HOST + localePath + asPath,
    ...(images?.length && { image: images[0].image }),
  });

  const alternateLinks = useHreflang({
    locale,
    route: 'vehicle',
    params: query,
  });

  usePageViewEvent(name);

  const mileageInfo = uniqueVehicleInfo.milageCoUK || uniqueVehicleInfo.mileage ? {
    unitCode: uniqueVehicleInfo.milageCoUK ? 'SMI' : 'KMT',
    value: uniqueVehicleInfo.milageCoUK || uniqueVehicleInfo.mileage || undefined
  } : undefined;

  return (
    <Head>
      <title>{name}</title>
      <meta name="description" content={description} />
      {alternateLinks}
      {og}

      <script {...useSchema({
        name, description, breadcrumbs,
        extraSchema: [{
          '@type': 'Vehicle',
          '@id': `${process.env.NEXT_PUBLIC_HOST}${localePath}${cleanAsPathForCanonicalURL}`,
          name: `${vehicle.brand_name} ${vehicle.model_name} ${vehicle.version_name || ''}`,
          image: images?.length > 0 ? images[0].image : `${process.env.NEXT_PUBLIC_HOST}/img/schema-placeholder/no-image-${locale}.png`,
          description: `${vehicle.brand_name} ${vehicle.model_description || ''} ${vehicle.version_description || ''} ${vehicle.years}`,
          sku: sku,
          brand: { '@type': 'Brand', name: vehicle.brand_name },
          numberOfDoors: vehicle.door_nr,
          mileageFromOdometer: mileageInfo,
          fuelType: uniqueVehicleInfo.technical_info?.fuel_type || undefined,
          vehicleIdentificationNumber: uniqueVehicleInfo.vin || undefined,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            bestRating: 5,
            worstRating: 1,
            ratingCount: 8153,
            reviewCount: 8153,
          },
        }
        ],
      })} />
    </Head>
  );
};

export default VehicleSchema;
